export type ChangeCategory = 'added' | 'improved' | 'fixed'

export interface ChangelogItem {
  readonly key: string
  readonly category: ChangeCategory
}

export interface ChangelogRelease {
  readonly id: string
  readonly date: string
  readonly emoji?: string
  readonly summaryKey?: string
  readonly items: readonly ChangelogItem[]
}

// Prepend releases with a new, permanent ID; editing copy does not announce again.
export const changelogReleases: readonly ChangelogRelease[] = [
  {
    id: 'update20261031',
    date: '2026-10-31',
    items: [
      { key: 'gcwPercentageExp', category: 'added' },
      { key: 'historyRecovery', category: 'added' },
      { key: 'widgetSkins', category: 'improved' },
      { key: 'precisionCalibration', category: 'improved' },
      { key: 'personalMapReference', category: 'improved' },
      { key: 'experienceRecognition', category: 'fixed' },
      { key: 'buffReminders', category: 'fixed' },
      { key: 'captureRecovery', category: 'fixed' },
    ],
  },
  {
    id: 'update20261027',
    date: '2026-10-27',
    items: [
      { key: 'personalMapReference', category: 'added' },
      { key: 'rankEntry', category: 'added' },
      { key: 'liveDashboard', category: 'improved' },
      { key: 'buffAlerts', category: 'improved' },
      { key: 'expPreview', category: 'fixed' },
      { key: 'mesosRecognition', category: 'fixed' },
      { key: 'resourceLoading', category: 'fixed' },
    ],
  },
  {
    id: 'update20261025',
    date: '2026-10-25',
    items: [
      { key: 'customBuffAudio', category: 'added' },
      { key: 'mapEfficiency', category: 'improved' },
      { key: 'rankFilters', category: 'improved' },
      { key: 'buffSearch', category: 'improved' },
      { key: 'buffCountdown', category: 'fixed' },
      { key: 'buffIcons', category: 'fixed' },
    ],
  },
  {
    id: 'update20261023',
    date: '2026-10-23',
    items: [
      { key: 'multiJobFilter', category: 'added' },
      { key: 'buffManagement', category: 'added' },
      { key: 'sessionStart', category: 'improved' },
      { key: 'gameRecognition', category: 'fixed' },
      { key: 'expAccounting', category: 'fixed' },
      { key: 'buffRecognition', category: 'fixed' },
    ],
  },
  {
    id: 'update20261020',
    date: '2026-10-20',
    items: [
      { key: 'manboVoice', category: 'added' },
      { key: 'manualBuffTimer', category: 'added' },
      { key: 'dropSellPrice', category: 'added' },
      { key: 'sessionAutosave', category: 'improved' },
      { key: 'runtimePerformance', category: 'improved' },
      { key: 'dashboardCards', category: 'improved' },
      { key: 'buffCountdown', category: 'fixed' },
      { key: 'recognitionAccuracy', category: 'fixed' },
    ],
  },
  {
    id: 'update20261014',
    date: '2026-10-14',
    items: [
      { key: 'experienceSettings', category: 'added' },
      { key: 'attributeRecognition', category: 'added' },
      { key: 'recognitionPerformance', category: 'improved' },
      { key: 'journeyChart', category: 'improved' },
      { key: 'gcwRecognition', category: 'improved' },
      { key: 'buffCountdown', category: 'fixed' },
      { key: 'widgetDisplay', category: 'fixed' },
      { key: 'expChartContinuity', category: 'fixed' },
      { key: 'captureFrameRate', category: 'fixed' },
    ],
  },
  {
    id: 'update20261010',
    date: '2026-10-10',
    items: [
      { key: 'historyTools', category: 'improved' },
      { key: 'widgetWindowModes', category: 'added' },
      { key: 'gcwRecognition', category: 'improved' },
      { key: 'buffDetection', category: 'fixed' },
    ],
  },
  {
    id: 'update20261008',
    date: '2026-10-08',
    items: [
      { key: 'gcwSupport', category: 'added' },
      { key: 'defaultWidget', category: 'improved' },
      { key: 'compactWidget', category: 'improved' },
    ],
  },
  {
    id: 'update20261005',
    date: '2026-10-05',
    items: [
      { key: 'historyStatistics', category: 'improved' },
      { key: 'expRecognition', category: 'fixed' },
      { key: 'expRateExplanation', category: 'improved' },
      { key: 'buffRecognition', category: 'improved' },
      { key: 'widgetExperience', category: 'improved' },
    ],
  },
  {
    id: 'update20260930',
    date: '2026-09-30',
    items: [
      { key: 'currentMap', category: 'added' },
      { key: 'dropDetails', category: 'added' },
      { key: 'liveLayout', category: 'improved' },
      { key: 'skinPreview', category: 'improved' },
      { key: 'widgetRecovery', category: 'fixed' },
      { key: 'shortSessions', category: 'fixed' },
    ],
  },
  {
    id: 'update20260928',
    date: '2026-09-28',
    items: [
      { key: 'realtimeRateChart', category: 'added' },
      { key: 'widgetLayout', category: 'improved' },
    ],
  },
  {
    id: 'update20260924',
    date: '2026-09-24',
    items: [
      { key: 'buffAlerts', category: 'added' },
      { key: 'rateUnits', category: 'added' },
      { key: 'lightweightOcr', category: 'added' },
      { key: 'artaleSupport', category: 'added' },
      { key: 'newLanguages', category: 'added' },
      { key: 'userExperience', category: 'improved' },
      { key: 'recognitionAccuracy', category: 'improved' },
      { key: 'widgetDisplay', category: 'fixed' },
      { key: 'styleIssues', category: 'fixed' },
    ],
  },
  {
    id: 'update20260918',
    date: '2026-09-18',
    items: [
      { key: 'rankPageRebuild', category: 'added' },
      { key: 'browserCompatibility', category: 'improved' },
      { key: 'captureTimeout', category: 'improved' },
    ],
  },
  {
    id: 'update20260914',
    date: '2026-09-14',
    items: [
      { key: 'realtimeExpRate', category: 'added' },
      { key: 'firstUseAutoLocate', category: 'added' },
      { key: 'clearerMetrics', category: 'improved' },
    ],
  },
  {
    id: 'update20260913',
    date: '2026-09-13',
    items: [
      { key: 'autoLocateMesos', category: 'added' },
      { key: 'factoryReset', category: 'added' },
      { key: 'pipWindowSize', category: 'added' },
      { key: 'calibrationPreview', category: 'improved' },
      { key: 'recoveryPaths', category: 'improved' },
      { key: 'highDpiRecognition', category: 'fixed' },
      { key: 'upgradeExpAccounting', category: 'fixed' },
    ],
  },
  {
    id: 'update20260910',
    date: '2026-09-10',
    items: [
      { key: 'newLayoutAutoLocate', category: 'added' },
      { key: 'rankMapTip', category: 'added' },
      { key: 'calibrationSimpler', category: 'improved' },
      { key: 'jobLineRecognition', category: 'improved' },
    ],
  },
  {
    id: 'update20260909',
    date: '2026-09-09',
    items: [
      { key: 'recognitionPerformance', category: 'added' },
      { key: 'hpMpRates', category: 'improved' },
      { key: 'lowFrequencyMesos', category: 'improved' },
      { key: 'mapRomanSuffix', category: 'fixed' },
    ],
  },
  {
    id: 'update20260908',
    date: '2026-09-08',
    items: [
      { key: 'streamSkins', category: 'added' },
      { key: 'streamConnect', category: 'added' },
      { key: 'allLevelRankings', category: 'added' },
      { key: 'localDiagnostics', category: 'added' },
      { key: 'screenCaptureFallback', category: 'improved' },
      { key: 'recognitionRecovery', category: 'improved' },
      { key: 'captureFailureGuidance', category: 'fixed' },
      { key: 'jobRecognition', category: 'fixed' },
    ],
  },
  {
    id: 'update20260907',
    date: '2026-09-07',
    items: [
      { key: 'mapRanking', category: 'added' },
      { key: 'widgetSkins', category: 'added' },
      { key: 'efficiencyChart', category: 'added' },
      { key: 'calibrationExamples', category: 'added' },
      { key: 'autoRegion', category: 'improved' },
      { key: 'recognitionPerformance', category: 'improved' },
      { key: 'startFeedback', category: 'improved' },
      { key: 'responsiveWidget', category: 'improved' },
      { key: 'firefoxCompatibility', category: 'fixed' },
      { key: 'recordingStart', category: 'fixed' },
    ],
  },
  {
    id: 'update20260904',
    date: '2026-09-04',
    emoji: '✨',
    items: [
      { key: 'efficiencyTrend', category: 'added' },
      { key: 'expRate', category: 'improved' },
      { key: 'startupSpeed', category: 'improved' },
      { key: 'windowSelection', category: 'improved' },
      { key: 'mesosAccuracy', category: 'fixed' },
    ],
  },
  {
    id: 'launch20260902',
    date: '2026-09-02',
    emoji: '🍁',
    summaryKey: 'changelog.releases.launch20260902.summary',
    items: [],
  },
]

export const latestRelease = changelogReleases[0]

export function formatReleaseDate(value: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${value}T00:00:00Z`))
}
